import { useMemo, useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient } from '../../providers/auth-provider';
import { useSandboxFleetSettings, useSandboxPools, useSandboxRegistries } from '../../hooks/use-sandbox-fleet';

import {
  Card,
  Empty,
  Metric,
  Pill,
  StatusDot,
  Tabs,
  Td,
  Th,
  fmtWhen,
  inputStyle,
  mono,
  primaryButton,
  secondaryButton,
  tableStyle,
  theadRowStyle,
} from '../proxy-vault/ui';
import { AddRegistryModal } from './AddRegistryModal';

import type { OpenTabFn } from '../../shell/types';
import type { SandboxPoolResponse, SandboxRegistryResponse } from '../../lib/api-client';

/**
 * Sandbox Fleet — the operator surface over demand-driven cell pools.
 *
 * Four pages over one system: Overview answers "what is the fleet doing",
 * Pools is the live inventory (a row opens the pool as its own workspace tab),
 * Registries is the pull-credential store, Settings the fleet-wide defaults.
 * Same shape as the Proxy Vault section, deliberately.
 */
export function SandboxFleetView({ openTab }: { openTab: OpenTabFn }) {
  const [page, setPage] = useState<'overview' | 'pools' | 'registries' | 'settings'>('overview');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'pools', label: 'Pools' },
          { id: 'registries', label: 'Registries' },
          { id: 'settings', label: 'Settings' },
        ]}
        active={page}
        onChange={(id) => setPage(id as typeof page)}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {page === 'overview' && <OverviewPage />}
        {page === 'pools' && <PoolsPage openTab={openTab} />}
        {page === 'registries' && <RegistriesPage />}
        {page === 'settings' && <SettingsPage />}
      </div>
    </div>
  );
}

function OverviewPage() {
  const { pools, status } = useSandboxPools();
  const { registries } = useSandboxRegistries();
  const totals = useMemo(() => {
    const ready = pools.filter((p) => p.status === 'ready');
    return {
      pools: pools.length,
      readyReplicas: pools.reduce((n, p) => n + (p.ready_replicas ?? 0), 0),
      active: pools.reduce((n, p) => n + p.active, 0),
      retired: pools.filter((p) => p.status === 'retired').length,
      readyPools: ready.length,
    };
  }, [pools]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 900 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Metric value={totals.pools} label="pools" />
        <Metric value={totals.readyPools} label="serving" />
        <Metric value={totals.readyReplicas} label="warm pods" />
        <Metric value={totals.active} label="active leases" />
        <Metric value={registries.length} label="registries" />
      </div>
      <Card title="How this works">
        <div style={{ fontSize: 13, color: 'var(--text-soft)', lineHeight: 1.6 }}>
          A pool appears automatically when a conversation pins a sandbox bundle
          (<span style={{ fontFamily: mono }}>runtime.sandbox_image</span>) the fleet has not seen —
          AgentOS deploys announce their bundle this way — or when you pre-warm one from the Pools
          page. Warm pods await claims; <b>Active</b> pods are serving a lease. Retire drains a pool
          (no new claims, leases finish); Delete removes it — pinned conversations fall back to the
          environment&apos;s static pool. Pull credentials live under Registries.
        </div>
      </Card>
      {status === 'error' && <Empty>Fleet API unreachable — is the feature enabled on this environment?</Empty>}
    </div>
  );
}

function poolTone(p: SandboxPoolResponse): 'warn' | 'mute' | undefined {
  if (p.status === 'retired' || p.status === 'deleting') return 'mute';
  if (p.status === 'provisioning' || p.worker_sdk_incompatible) return 'warn';
  return undefined;
}

function PoolsPage({ openTab }: { openTab: OpenTabFn }) {
  const api = useApiClient();
  const { pools, status, error, refresh } = useSandboxPools();
  const [prewarmImage, setPrewarmImage] = useState('');
  const [prewarmAgentType, setPrewarmAgentType] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const prewarm = async () => {
    if (!prewarmImage.trim() || !prewarmAgentType.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.prewarmSandboxPool({ image: prewarmImage.trim(), agent_type: prewarmAgentType.trim() });
      setPrewarmImage('');
      refresh();
    } catch (err) {
      setActionError(describeApiError(err, String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 760 }}>
        <input
          data-testid="fleet-prewarm-image"
          style={{ ...inputStyle, flex: 1, fontFamily: mono }}
          placeholder="Pre-warm a bundle: full image reference (host/repo:tag)"
          value={prewarmImage}
          onChange={(e) => setPrewarmImage(e.target.value)}
        />
        <input
          data-testid="fleet-prewarm-agent-type"
          style={{ ...inputStyle, width: 180, fontFamily: mono }}
          placeholder="agent_type"
          title="The cell family the pool serves — pools are family-keyed (see the tenant's cell_families settings)"
          value={prewarmAgentType}
          onChange={(e) => setPrewarmAgentType(e.target.value)}
        />
        <button
          type="button"
          data-testid="fleet-prewarm-submit"
          style={{ ...primaryButton, opacity: prewarmImage.trim() && prewarmAgentType.trim() && !busy ? 1 : 0.5 }}
          disabled={!prewarmImage.trim() || !prewarmAgentType.trim() || busy}
          onClick={() => void prewarm()}
        >
          {busy ? 'Warming…' : 'Pre-warm'}
        </button>
      </div>
      {actionError && <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)' }}>{actionError}</div>}
      {status === 'error' && <Empty>{error ?? 'Failed to load pools'}</Empty>}
      {status === 'success' && pools.length === 0 && (
        <Empty>
          No dynamic pools yet. Pools appear when a conversation pins a bundle — or pre-warm one now.
        </Empty>
      )}
      {pools.length > 0 && (
        <table style={tableStyle} data-testid="fleet-pools-table">
          <thead>
            <tr style={theadRowStyle}>
              <Th first>Bundle</Th>
              <Th>Status</Th>
              <Th>Ready</Th>
              <Th>Active</Th>
              <Th>Idle</Th>
              <Th>Worker SDK</Th>
              <Th>First ready</Th>
            </tr>
          </thead>
          <tbody>
            {pools.map((p) => (
              <tr
                key={p.id}
                style={{ cursor: 'pointer' }}
                data-testid={`fleet-pool-row-${p.bundle_key}`}
                onClick={() =>
                  openTab('sandbox-fleet-pool', { poolId: p.id }, p.bundle_ref.split('/').pop() ?? p.bundle_key)
                }
              >
                <Td first>
                  <span style={{ fontFamily: mono, fontSize: 12 }}>{p.bundle_ref}</span>
                </Td>
                <Td>
                  <StatusDot status={p.status === 'ready' ? 'completed' : p.status === 'provisioning' ? 'running' : 'incomplete'} />
                  <Pill tone={poolTone(p)}>{p.status}</Pill>
                  {p.worker_sdk_incompatible && <Pill tone="warn">worker SDK below min</Pill>}
                </Td>
                <Td>
                  {p.ready_replicas ?? '—'}/{p.replicas}
                </Td>
                <Td>{p.active}</Td>
                <Td>{p.idle}</Td>
                <Td>
                  <span style={{ fontFamily: mono, fontSize: 12 }}>{p.worker_sdk_version ?? '—'}</span>
                </Td>
                <Td>{fmtWhen(p.first_ready_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RegistriesPage() {
  const api = useApiClient();
  const { registries, status, error, refresh } = useSandboxRegistries();
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const rotate = async (r: SandboxRegistryResponse) => {
    // Native prompt keeps the write-only property: the value exists only in
    // this dialog for the moment of entry.
    const secret = window.prompt(`New secret for ${r.name} (${r.login_server}):`);
    if (!secret) return;
    setActionError(null);
    try {
      await api.updateSandboxRegistry(r.id, { secret });
      refresh();
    } catch (err) {
      setActionError(describeApiError(err, String(err)));
    }
  };

  const toggle = async (r: SandboxRegistryResponse) => {
    setActionError(null);
    try {
      await api.updateSandboxRegistry(r.id, { status: r.status === 'active' ? 'disabled' : 'active' });
      refresh();
    } catch (err) {
      setActionError(describeApiError(err, String(err)));
    }
  };

  const remove = async (r: SandboxRegistryResponse) => {
    if (!window.confirm(`Remove registry ${r.name}? Its vault material and pull secret are deleted.`)) return;
    setActionError(null);
    try {
      await api.deleteSandboxRegistry(r.id);
      refresh();
    } catch (err) {
      setActionError(describeApiError(err, String(err)));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <button type="button" data-testid="fleet-add-registry" style={primaryButton} onClick={() => setAdding(true)}>
          Register a registry…
        </button>
      </div>
      {actionError && <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)' }}>{actionError}</div>}
      {status === 'error' && <Empty>{error ?? 'Failed to load registries'}</Empty>}
      {status === 'success' && registries.length === 0 && (
        <Empty>Register a registry so the fleet can pull bundle images.</Empty>
      )}
      {registries.length > 0 && (
        <table style={tableStyle} data-testid="fleet-registries-table">
          <thead>
            <tr style={theadRowStyle}>
              <Th first>Name</Th>
              <Th>Login server</Th>
              <Th>Auth</Th>
              <Th>Status</Th>
              <Th>Pools</Th>
              <Th>Last used</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {registries.map((r) => (
              <tr key={r.id}>
                <Td first>{r.name}</Td>
                <Td>
                  <span style={{ fontFamily: mono, fontSize: 12 }}>{r.login_server}</span>
                </Td>
                <Td>{r.auth_type}</Td>
                <Td>
                  <Pill tone={r.status === 'active' ? undefined : 'mute'}>{r.status}</Pill>
                </Td>
                <Td>{r.pools_referencing}</Td>
                <Td>{fmtWhen(r.last_used_at)}</Td>
                <Td>
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <button type="button" style={secondaryButton} onClick={() => void rotate(r)}>
                      Rotate
                    </button>
                    <button type="button" style={secondaryButton} onClick={() => void toggle(r)}>
                      {r.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                    <button type="button" style={secondaryButton} onClick={() => void remove(r)}>
                      Remove
                    </button>
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {adding && (
        <AddRegistryModal
          onCancel={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function SettingsPage() {
  const api = useApiClient();
  const { settings, status, error, refresh } = useSandboxFleetSettings();
  const [defaultReplicas, setDefaultReplicas] = useState<string>('');
  const [retentionKeep, setRetentionKeep] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.putSandboxFleetSettings({
        ...(defaultReplicas !== '' ? { default_replicas: Number(defaultReplicas) } : {}),
        ...(retentionKeep !== '' ? { retention_keep: Number(retentionKeep) } : {}),
      });
      setDefaultReplicas('');
      setRetentionKeep('');
      refresh();
    } catch (err) {
      setSaveError(describeApiError(err, String(err)));
    } finally {
      setSaving(false);
    }
  };

  if (status === 'error') return <Empty>{error ?? 'Failed to load settings'}</Empty>;
  if (!settings) return <Empty>Loading…</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
      <Card title="Fleet defaults">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
          <label style={{ color: 'var(--text-soft)' }}>
            Default pool size for new bundles (now {settings.default_replicas})
            <input
              data-testid="fleet-settings-replicas"
              style={{ ...inputStyle, width: 120, marginLeft: 10 }}
              type="number"
              min={0}
              max={50}
              placeholder={String(settings.default_replicas)}
              value={defaultReplicas}
              onChange={(e) => setDefaultReplicas(e.target.value)}
            />
          </label>
          <label style={{ color: 'var(--text-soft)' }}>
            Keep last N bundles warm per agent type (now {settings.retention_keep})
            <input
              data-testid="fleet-settings-retention"
              style={{ ...inputStyle, width: 120, marginLeft: 10 }}
              type="number"
              min={1}
              max={50}
              placeholder={String(settings.retention_keep)}
              value={retentionKeep}
              onChange={(e) => setRetentionKeep(e.target.value)}
            />
          </label>
          {saveError && <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)' }}>{saveError}</div>}
          <div>
            <button
              type="button"
              data-testid="fleet-settings-save"
              style={{ ...primaryButton, opacity: saving || (defaultReplicas === '' && retentionKeep === '') ? 0.5 : 1 }}
              disabled={saving || (defaultReplicas === '' && retentionKeep === '')}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
